import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  isManager as isManagerRoles,
  Role,
  type AuthenticatedUser,
  type ImpersonatorInfo,
} from '@ciimar/shared';
import { api, loginUrl } from '../api/client';
import { armIntro } from '../lib/intro';

interface AuthState {
  user: AuthenticatedUser | null;
  loading: boolean;
  isManager: boolean;
  isResearcher: boolean;
  /** True when this account may impersonate users (IT admin, not impersonating). */
  isAdminIt: boolean;
  /** The real admin behind the session when impersonating, else null. */
  impersonator: ImpersonatorInfo | null;
  /** Redirects to Microsoft sign-in (entra provider). */
  login: (returnTo?: string) => void;
  /** Username/password sign-in (local / ldap providers). */
  passwordLogin: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Ends the current impersonation and reloads as the real admin. */
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdminIt, setIsAdminIt] = useState(false);

  useEffect(() => {
    api
      .me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Resolve the IT-admin capability once signed in (false while impersonating).
  useEffect(() => {
    if (!user) {
      setIsAdminIt(false);
      return;
    }
    let active = true;
    api
      .adminItStatus()
      .then((s) => active && setIsAdminIt(s.isAdminIt))
      .catch(() => active && setIsAdminIt(false));
    return () => {
      active = false;
    };
  }, [user]);

  const stopImpersonation = useCallback(async () => {
    try {
      await api.stopImpersonation();
    } finally {
      // Full reload so every context refetches as the real admin.
      window.location.href = '/';
    }
  }, []);

  const login = useCallback((returnTo?: string) => {
    armIntro(); // play the dashboard entrance once after this sign-in
    window.location.href = loginUrl(returnTo);
  }, []);

  const passwordLogin = useCallback(async (username: string, password: string) => {
    armIntro(); // play the dashboard entrance once after this sign-in
    setUser(await api.passwordLogin(username, password));
  }, []);

  const logout = useCallback(async () => {
    let logoutUrl: string | undefined;
    try {
      ({ logoutUrl } = await api.logout());
    } catch {
      /* clear locally regardless */
    }
    setUser(null);
    window.location.href = logoutUrl ?? '/login';
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isManager: user ? isManagerRoles(user.roles) : false,
      isResearcher: user?.roles.includes(Role.Researcher) ?? false,
      isAdminIt,
      impersonator: user?.impersonator ?? null,
      login,
      passwordLogin,
      logout,
      stopImpersonation,
    }),
    [user, loading, isAdminIt, login, passwordLogin, logout, stopImpersonation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
